"use client";

import Link from "next/link";
import { useState, useTransition } from "react";
import { useCommentsContext } from "@/components/comments-list-client";
import {
	Avatar,
	HeartIcon,
} from "@/components/editorial-system/editorial-primitives";
import {
	type CommentTarget,
	commentTargetIds,
	EditorialReplyForm,
	REPLY_INPUT_ID,
} from "@/components/editorial-system/post/editorial-reply-form";
import { EmojiReactions } from "@/components/editorial-system/reactions";
import { TimeAgo } from "@/components/editorial-system/time-ago";
import { SafeHtmlRenderer } from "@/components/safe-html-renderer";
import { deleteCommentAction } from "@/lib/actions/comments";
import { toggleCommentReactionAction } from "@/lib/actions/reactions";
import { voteCommentAction } from "@/lib/actions/votes";
import { scheduleRouterRefresh } from "@/lib/client/app-router-refresh";
import { useReplyStore } from "@/lib/state/reply-store";
import type { CommentWithDetails } from "@/types/database";

function countReplies(comment: CommentWithDetails): number {
	return (comment.replies ?? []).reduce(
		(sum, reply) => sum + (reply.is_deleted ? 0 : 1) + countReplies(reply),
		0,
	);
}

function focusReplyForm() {
	const input = document.getElementById(REPLY_INPUT_ID);
	if (!input) return;
	input.scrollIntoView({ behavior: "smooth", block: "center" });
	input.focus({ preventScroll: true });
}

/**
 * One reply — who and when, what they wrote, likes, then "Reply to …" — with
 * its nested replies rendered after it as indented siblings.
 */
function ThreadReply({
	comment,
	target,
	currentUserId,
	depth,
	isTop,
}: {
	comment: CommentWithDetails;
	target: CommentTarget;
	currentUserId?: string | null;
	depth: number;
	isTop: boolean;
}) {
	const [upvotes, setUpvotes] = useState(comment.upvotes ?? 0);
	const [userVote, setUserVote] = useState<-1 | 1 | null>(
		comment.user_vote ?? null,
	);
	const [error, setError] = useState<string | null>(null);
	const [isVoting, startVote] = useTransition();
	const [isDeleting, startDelete] = useTransition();
	const startReply = useReplyStore((state) => state.startReply);
	const clearReply = useReplyStore((state) => state.clearReply);
	const activeCommentId = useReplyStore((state) => state.activeCommentId);

	if (comment.is_deleted) return null;

	const who = comment.author?.username?.trim()
		? `@${comment.author.username.trim()}`
		: "Member";
	const isOwn = Boolean(currentUserId && comment.author?.id === currentUserId);
	const isPending = comment.id < 0;
	const replies = (comment.replies ?? []).filter((reply) => !reply.is_deleted);
	const replyCount = countReplies(comment);

	function like() {
		if (!currentUserId || isPending) return;
		const next = userVote === 1 ? null : 1;
		setUserVote(next);
		setUpvotes((count) => (next === 1 ? count + 1 : Math.max(0, count - 1)));
		startVote(async () => {
			const result = await voteCommentAction(comment.id, next);
			if (!result.success) {
				setUserVote(comment.user_vote ?? null);
				setUpvotes(comment.upvotes ?? 0);
			} else if (result.data) {
				setUpvotes(result.data.upvotes);
				setUserVote(result.data.user_vote);
			}
		});
	}

	function replyToThis() {
		startReply({ commentId: comment.id, author: who });
		focusReplyForm();
	}

	function remove() {
		if (!window.confirm("Delete this reply? This cannot be undone.")) return;
		startDelete(async () => {
			setError(null);
			const result = await deleteCommentAction({
				commentId: comment.id,
				...commentTargetIds(target),
			});
			if (!result.success) {
				setError(result.error ?? "Couldn’t delete this reply.");
				return;
			}
			if (activeCommentId === comment.id) clearReply();
			scheduleRouterRefresh();
		});
	}

	return (
		<>
			<article
				className="ed-reply"
				data-comment-id={String(comment.id)}
				data-depth={depth}
			>
				<div className="ed-meta ed-row" style={{ gap: 10 }}>
					{depth > 0 ? (
						<span aria-hidden className="ed-secondary">
							↳
						</span>
					) : null}
					<Avatar
						person={{
							name: comment.author?.username || "Member",
							avatarUrl: comment.author?.avatar_url,
						}}
					/>
					<span>
						{comment.author?.username ? (
							<Link href={`/users/${comment.author.username}`}>{who}</Link>
						) : (
							who
						)}{" "}
						<span className="ed-secondary">
							· <TimeAgo iso={comment.created_at} />
						</span>
					</span>
					{isTop ? <span className="ed-secondary">· Top reply</span> : null}
				</div>
				<div className="ed-reply-prose">
					<SafeHtmlRenderer html={comment.body ?? ""} typography="reading" />
				</div>
				<div className="ed-reply-actions">
					<div className="ed-meta ed-row" style={{ gap: 12 }}>
						<button
							type="button"
							className="ed-button-text ed-row"
							style={{ gap: 4 }}
							onClick={like}
							aria-pressed={userVote === 1}
							disabled={!currentUserId || isVoting || isPending}
						>
							<HeartIcon filled />
							{upvotes}
							<span className="sr-only">
								{userVote === 1 ? " Unlike reply" : " Like reply"}
							</span>
						</button>
						{depth === 0 && replyCount ? (
							<span>
								{replyCount} {replyCount === 1 ? "reply" : "replies"}
							</span>
						) : null}
						<EmojiReactions
							label="reply"
							summary={comment.reaction_summary ?? []}
							mine={comment.user_reactions ?? []}
							signedIn={Boolean(currentUserId)}
							onToggle={(emoji, has) =>
								toggleCommentReactionAction(comment.id, emoji, has)
							}
						/>
					</div>
					{currentUserId && !isPending ? (
						<div className="ed-row" style={{ gap: 16 }}>
							<button
								type="button"
								className="ed-button-text ed-ui ed-secondary"
								onClick={replyToThis}
								aria-controls={REPLY_INPUT_ID}
							>
								Reply to {who}
							</button>
							{isOwn ? (
								<button
									type="button"
									className="ed-button-text ed-ui ed-secondary"
									onClick={remove}
									disabled={isDeleting}
								>
									{isDeleting ? "Deleting…" : "Delete"}
								</button>
							) : null}
						</div>
					) : null}
				</div>
				{error ? (
					<p role="alert" className="ed-ui ed-secondary ed-flush">
						{error}
					</p>
				) : null}
			</article>
			{replies.map((reply) => (
				<ThreadReply
					key={reply.id}
					comment={reply}
					target={target}
					currentUserId={currentUserId}
					depth={depth + 1}
					isTop={false}
				/>
			))}
		</>
	);
}

/**
 * Replies on a post, track or story in the editorial column, and the reply
 * form at the end.
 * Uses the existing comment engine (`CommentsListClient` context) for
 * optimistic replies; only the presentation is editorial.
 */
export function PostReplies({
	target,
	replyToLabel,
	currentUserId,
	returnTo,
	canReply = true,
	gate,
	showList = true,
}: {
	target: CommentTarget;
	replyToLabel: string;
	currentUserId?: string | null;
	/** Where Log in returns to. */
	returnTo: string;
	/** False when the viewer is signed in but can't take part (e.g. not a member). */
	canReply?: boolean;
	/** Shown instead of the form when the viewer can't reply. */
	gate?: React.ReactNode;
	/** False when the viewer may not read replies (e.g. member-only stories). */
	showList?: boolean;
}) {
	const { displayComments, threadsContainerRef } = useCommentsContext();
	const visible = displayComments.filter((comment) => !comment.is_deleted);
	const top = [...visible].sort(
		(a, b) => (b.upvotes ?? 0) - (a.upvotes ?? 0),
	)[0];
	const topId = top && (top.upvotes ?? 0) > 1 ? top.id : null;

	return (
		<>
			{showList ? (
				<div ref={threadsContainerRef} className="ed-replies">
					{visible.length ? (
						visible.map((comment) => (
							<div key={comment.id} className="ed-reply-thread">
								<ThreadReply
									comment={comment}
									target={target}
									currentUserId={currentUserId}
									depth={0}
									isTop={comment.id === topId}
								/>
							</div>
						))
					) : (
						<p className="ed-body ed-secondary ed-flush">No replies yet.</p>
					)}
				</div>
			) : null}

			<section id="post-reply" aria-label="Reply" className="ed-reply-section">
				{currentUserId && canReply ? (
					<EditorialReplyForm target={target} replyToLabel={replyToLabel} />
				) : gate ? (
					gate
				) : (
					<p className="ed-ui ed-flush">
						<Link
							href={`/login?redirectedFrom=${encodeURIComponent(returnTo)}`}
							className="ed-link"
						>
							Log in
						</Link>{" "}
						to join the conversation.
					</p>
				)}
			</section>
		</>
	);
}
